/**
 * The unauthenticated seam.
 *
 * `POST /auth/signup` runs before any session exists, and `ApiClient` is
 * provided to the app as an authenticated dependency — so signup needs a seam
 * that does not assume a caller. This is it, and it is deliberately tiny: one
 * client with no token middleware, one method. Everything authenticated stays
 * on `ApiClient`.
 *
 * Signup goes to the API, not to Cognito. The API creates the `orgs` and
 * `users` rows and stamps the three custom claims onto the account in one
 * transaction; `CognitoUserPool.signUp` from the browser would create an
 * account with no rows and no claims — one that authenticates and is then
 * refused by every route.
 */

import { createMatchPointClient } from '@match-point/api-client-ts'

import { AuthError, type SignUpOutcome } from '../auth/contract'
import { networkError, toApiError } from './errors'

export interface PublicApi {
  signUp: (input: {
    email: string
    password: string
    displayName: string
    orgName: string
    role: 'provider_admin'
  }) => Promise<SignUpOutcome>
  /**
   * `POST /auth/federated-signup`. Authenticated, but with a token the ordinary
   * `ApiClient` cannot use yet: it carries no account claims, which is the
   * point. So the token is passed in explicitly rather than read from a session
   * that does not exist.
   */
  federatedSignUp: (idToken: string, input: { orgName: string, displayName: string }) => Promise<void>
  /**
   * `POST /auth/staff-provision` (ADR 0016), on the staff API. The first staff
   * sign-in's token carries no account claims, so it is passed in explicitly, as
   * for `federatedSignUp`. No body: who this is comes entirely from the token.
   */
  staffProvision: (idToken: string) => Promise<void>
}

export function createPublicApi (baseUrl: string): PublicApi {
  const client = createMatchPointClient({ baseUrl })

  return {
    staffProvision: async (idToken) => {
      const attempt = async () => {
        try {
          return await client.POST('/auth/staff-provision', {
            headers: { Authorization: `Bearer ${idToken}` }
          })
        } catch (cause) {
          throw Object.assign(
            new AuthError('Your staff account could not be set up just now. Please try again.', 'unavailable'),
            { cause: networkError(cause) }
          )
        }
      }

      // A 409 is retried once. Two tabs finishing the same first sign-in race on
      // the unique address, and the loser's retry finds the winner's row and
      // succeeds; a 409 that survives the retry is the server's real refusal.
      let result = await attempt()
      if (result.response.status === 409) result = await attempt()

      if (result.response.status === 409) {
        // The server says which: an address held by a customer account, or by an
        // older staff account outside Match Point's organization.
        throw new AuthError(toApiError(409, result.error).message, 'duplicate-email')
      }

      if (!result.response.ok) {
        throw new AuthError(toApiError(result.response.status, result.error).message, 'unavailable')
      }
    },

    federatedSignUp: async (idToken, input) => {
      let result
      try {
        result = await client.POST('/auth/federated-signup', {
          headers: { Authorization: `Bearer ${idToken}` },
          body: { org_name: input.orgName, display_name: input.displayName }
        })
      } catch (cause) {
        throw Object.assign(
          new AuthError('Your account could not be created just now. Please try again.', 'unavailable'),
          { cause: networkError(cause) }
        )
      }

      if (result.response.status === 409) {
        throw new AuthError(
          'An account already exists for that email address. Sign in with your email and password.',
          'duplicate-email'
        )
      }

      if (!result.response.ok) {
        const problem = toApiError(result.response.status, result.error)
        throw new AuthError(problem.message, result.response.status === 400 ? 'validation' : 'unavailable')
      }
    },

    signUp: async (input) => {
      let result
      try {
        result = await client.POST('/auth/signup', {
          body: {
            email: input.email,
            password: input.password,
            display_name: input.displayName,
            role: input.role,
            org_name: input.orgName
          }
        })
      } catch (cause) {
        throw Object.assign(
          new AuthError('Sign-up is unavailable. Please try again.', 'unavailable'),
          { cause: networkError(cause) }
        )
      }

      if (result.response.status === 409) {
        throw new AuthError('An account already exists for that email address.', 'duplicate-email')
      }

      if (!result.response.ok) {
        // This used to be a single line mapping EVERY remaining status to
        // "Sign-up is unavailable. Please try again." -- which covered a
        // validation 400, a throttle, and a genuine outage with one sentence
        // that was wrong for two of the three. The live cost, on 2026-09-22:
        // somebody was told the service was down because their password was
        // eight characters, and told to retry something that could never work.
        //
        // toApiError is the same reader the authenticated seam uses, so a
        // problem document is parsed once rather than twice with two opinions.
        const problem = toApiError(result.response.status, result.error)

        if (problem.detail.fields && Object.keys(problem.detail.fields).length > 0) {
          throw new AuthError(
            problem.message,
            'validation',
            problem.detail.fields
          )
        }

        // Retryable means a dependency was unavailable or throttled, which is
        // the only case where "please try again" is honest advice.
        throw new AuthError(
          problem.detail.retryable
            ? 'Sign-up is temporarily unavailable. Please try again in a moment.'
            : problem.message,
          'unavailable'
        )
      }

      return { status: 'confirmation-required', email: input.email }
    }
  }
}
